import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLanguages } from "../../../../../../context/LanguagesContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { useAuth } from "../../../../../../context/AuthContext";
import { useTranslation } from "../../../../../../context/TranslationContext";
import { updateAttributeTranslation } from "../../../../../../services/api/apiClient";
import {
  AttributeValues,
  TranslationResult,
  UpdateTranslation,
} from "../../../../../../services/api/types/portalConfiguration/translations";
import { brand, gray } from "../../../../../../theme/themePrimitives";
import { fromCamelCase } from "../../../../../../utils/helpers";

interface EditModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  translations: TranslationResult[];
  selectedAttribute: TranslationResult;
  setTranslationsArray: React.Dispatch<
    React.SetStateAction<TranslationResult[]>
  >;
}

export const EditModal: React.FC<EditModalProps> = ({
  openModal,
  setOpenModal,
  loading,
  translations,
  selectedAttribute,
  setTranslationsArray,
}) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const { languages, getLanguageDetails } = useLanguages();
  const { portals } = usePortal();
  const { auth } = useAuth();
  const { updateTranslationSection } = useTranslation();

  useEffect(() => {
    if (translations.length) {
      const initialValues = translations.reduce(
        (acc, t) => {
          if (t.languageCode) {
            acc[t.languageCode] = t.attributeValue || "";
          }
          return acc;
        },
        {} as Record<string, string>,
      );
      setEditedValues(initialValues);
    }
  }, [translations, openModal]);

  const handleInputChange = (langCode: string, newValue: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [langCode]: newValue,
    }));
  };

  const updateTranslation = async () => {
    const attributeValues: AttributeValues[] = Object.entries(editedValues).map(
      ([langCode, value]) => ({
        language: langCode,
        attributeValue: value,
      }),
    );
    try {
      setButtonLoading(true);
      if (auth.user && portals.selectedPortal) {
        const body: UpdateTranslation = {
          attributeKey: selectedAttribute.attributeKey,
          adminUser: auth.user.UserPortalUid,
          attributeValues: attributeValues,
        };
        await updateAttributeTranslation(
          portals.selectedPortal.portalUid,
          selectedAttribute.tabId,
          selectedAttribute.tabSectionKey,
          body,
        );
        setTranslationsArray((prev) =>
          prev.map((translation) => {
            if (translation.attributeKey === selectedAttribute.attributeKey) {
              const updatedValue = body.attributeValues.find(
                (v) => v.language === translation.languageCode,
              )?.attributeValue;
              if (updatedValue !== undefined) {
                return { ...translation, attributeValue: updatedValue };
              }
            }
            return translation;
          }),
        );
        const selectedLangValue = body.attributeValues.find(
          (v) => v.language === languages.selectedLanguage,
        )?.attributeValue;
        if (selectedLangValue !== undefined) {
          updateTranslationSection(
            selectedAttribute.tabId,
            selectedAttribute.tabSectionKey,
            { [selectedAttribute.attributeKey]: selectedLangValue },
          );
        }
      }
    } catch (error) {
      console.log("Error", error);
    } finally {
      setButtonLoading(false);
      setOpenModal(false);
    }
  };

  return (
    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "16px",
          width: "50%",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "30vh",
            }}
          >
            <CircularProgress size={100} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h5">
                {fromCamelCase(selectedAttribute.attributeKey)}
              </Typography>
              <IconButton
                sx={{
                  border: "none",
                }}
                onClick={() => setOpenModal(false)}
              >
                <CloseIcon sx={{ color: "#98A2B3" }} />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              sx={{
                paddingBottom: "1rem",
                borderBottom: `1px solid ${gray[300]}`,
              }}
              pb={"2rem"}
            >
              Translate text to selected languages
            </Typography>
            <Stack spacing={2} sx={{ marginTop: "1rem" }}>
              {translations.map((translation, index) => {
                return (
                  <Stack spacing={1} key={index}>
                    <Typography variant="subtitle2">
                      {getLanguageDetails(translation.languageCode!)?.name}
                    </Typography>
                    <TextField
                      fullWidth
                      id=""
                      type="text"
                      value={editedValues[translation.languageCode!] || ""}
                      onChange={(e) =>
                        handleInputChange(
                          translation.languageCode!,
                          e.target.value,
                        )
                      }
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                );
              })}
              <Divider />
              {buttonLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Button
                  onClick={updateTranslation}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: `${brand[600]} !important`,
                    color: `white`,
                    "&:hover": {
                      backgroundColor: `${brand[700]} !important`,
                    },
                  }}
                >
                  <Typography>Update</Typography>
                </Button>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Modal>
  );
};
